import ActionPatient from '../components/actionBooking'
import { CBookingStatusDes, CExamStatusDes, CRegexPatterns } from '../constants'
import { EBookingStatusColor, EExamStatusColor } from '../enums'
import { IBooking } from '../interfaces'
import { Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { Status } from 'components/atoms'
import Actions, { ActionType } from 'components/atoms/actions'
import { ActionsEditDelete } from 'constants/actionsType'
import { NO_DATA } from 'constants/common'
import { DATE_EMPTY } from 'constants/date'
import { useColumnFactory } from 'hooks'
import moment from 'moment'
import { ECanceledByUser } from 'pages/patientReceive/enums/eCanceledByUser'
import { IPatient } from 'pages/patientReceive/interfaces'
import { EBookingStatus } from 'pages/patientTransaction/enums'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { DivIndex } from 'styles/styledGlobal'
import { formatDate, getDateOfBirthString, getOldPatient } from 'utils'

const useColumnBooking = (
  offset: number,
  totalRecord: number,
  onAction: (type: ActionType, row: any) => void
) => {
  const { t } = useTranslation()
  const { createColumns } = useColumnFactory<any>()
  const getBookingColumns = (): ColumnsType<IBooking> => [
    {
      title: 'hash',
      dataIndex: 'hash',
      align: 'center',
      fixed: 'left',
      width: 100,
      render: (id, data: IBooking, index) => (
        <DivIndex>{index + 1 + offset}</DivIndex>
      )
    },
    {
      title: 'numeric-examination-schedule',
      dataIndex: 'booking_no',
      fixed: 'left',
      width: 90,
      render: (value, data: IBooking, index) =>
        value ? (
          <Link to={`/booking/receive/${data?.booking_id}`}>{value}</Link>
        ) : (
          NO_DATA
        )
    },
    {
      title: 'time',
      dataIndex: 'time',
      width: 195,
      render: (id, record) => {
        return `${formatDate(record?.from, 'HH:mm')} - ${formatDate(
          record.to,
          'HH:mm DD/MM/YYYY'
        )}`
      }
    },
    {
      title: 'schedule-code',
      dataIndex: 'booking_code',
      width: 130,
      render: (value, data: IBooking, index) => (
        <Link to={`/booking/receive/${data?.booking_id}`}>{value}</Link>
      )
    },
    {
      title: 'patient-code',
      dataIndex: 'patient',
      width: 120,
      render: (patient: IPatient) => (
        <Link to={`patient/${patient?.user_code?.replace('AMU', '')}`}>
          {patient?.user_code?.replace('AMU', '') || NO_DATA}
        </Link>
      )
    },
    {
      title: 'phone-number',
      dataIndex: 'patient',
      width: 120,
      render: (patient: IPatient) => patient?.phone_number || NO_DATA
    },
    {
      title: 'fullname',
      dataIndex: 'patient',
      render: (patient: IPatient) => patient?.full_name || NO_DATA,
      width: 260
    },
    {
      title: 'gender',
      dataIndex: 'patient',
      width: 80,
      render: (patient: IPatient) => t(`gender-${patient?.gender}`)
    },
    {
      title: 'old',
      dataIndex: 'patient',
      width: 60,
      render: (patient: IPatient) => {
        return getOldPatient(patient?.year) || NO_DATA
      }
    },

    {
      title: 'service',
      dataIndex: 'metadata',
      width: 200,
      render: (service) => {
        const serviceName =
          service?.initial_services?.services
            ?.map((service: any) => service?.name)
            ?.join(', ') || NO_DATA
        return <Tooltip title={serviceName}>{serviceName}</Tooltip>
      }
    },
    {
      title: 'major',
      dataIndex: 'metadata',
      width: 200,
      render: (service) => {
        const serviceName =
          Array.from(
            new Set(
              service?.initial_services?.services?.map(
                (service: any) => service?.major_name
              )
            )
          )?.join(', ') || NO_DATA
        return <Tooltip title={serviceName}>{serviceName}</Tooltip>
      }
    },
    {
      title: 'booking-status',
      dataIndex: 'status',
      width: 150,
      render: (status, data: IBooking) => {
        const cancelBy =
          data.canceled_by_user === ECanceledByUser.PATIENT
            ? data?.canceled_by_name
            : t(`${data?.canceled_by_user?.toLowerCase()}`)
        const reason = (
          <>
            <div>
              <strong>{`${t('reason')}: `}</strong>
              {data.cancel_reason?.replace(CRegexPatterns.systemPrefix, '')}
            </div>
            <div>
              <strong>{`${t('cancel-by')}: `}</strong>
              {cancelBy || '--'}
            </div>
          </>
        )
        return (
          <Status
            statusColor={EBookingStatusColor[status]}
            title={t(CBookingStatusDes[status])}
            reason={status === EBookingStatus.Canceled ? reason : undefined}
          />
        )
      }
    },
    {
      title: 'examination-status',
      dataIndex: 'exam_status',
      width: 150,
      render: (status, data: IBooking) => {
        if (
          ![EBookingStatus.Finished, EBookingStatus.InProgress].includes(
            data.status
          )
        )
          return '--'
        return (
          <Status
            statusColor={EExamStatusColor[status]}
            title={t(CExamStatusDes[status])}
          />
        )
      }
    },
    {
      title: 'action',
      dataIndex: 'status',
      fixed: 'right',
      align: 'center',
      width: 100,
      render: (status, row) => {
        const actions = [...ActionsEditDelete]
        const diffHours =
          (moment(row?.from).valueOf() - moment().valueOf()) / (1000 * 60 * 60)
        const isNotConfirmedOrCreated =
          status !== EBookingStatus.Confirmed &&
          status !== EBookingStatus.Created
        const isLessThanFourHours = diffHours < 4
        if (isNotConfirmedOrCreated || isLessThanFourHours) {
          actions[0] = { ...actions[0], disabled: true }
          actions[1] = { ...actions[1], disabled: true }
        }
        return (
          <Actions
            actions={actions}
            justifyContent="center"
            onClick={(actionType) => onAction(actionType, row)}
          />
        )
      }
    }
  ]

  const getPatientColumns = (): ColumnsType<IPatient> => [
    {
      title: 'hash',
      dataIndex: 'hash',
      align: 'center',
      fixed: 'left',
      width: 100,
      render: (id, data: IPatient, index) => (
        <DivIndex>{index + 1 + offset}</DivIndex>
      )
    },
    {
      title: 'pa-code',
      dataIndex: 'user_code',
      fixed: 'left',
      width: 120,
      render: (value, data: IPatient, index) => (
        <Link to={`patient/${data?.user_id}`}>{value.replace('AMU', '')}</Link>
      )
    },
    {
      title: 'fullname',
      dataIndex: 'full_name',
      width: 240
    },
    {
      title: 'birthday-date',
      dataIndex: 'date_of_birth',
      width: 100,
      render: (birthday, patient) =>
        getDateOfBirthString(patient?.date, patient?.month, patient?.year)
    },
    {
      title: 'phone-number',
      dataIndex: 'phone_number',
      width: 120
    },
    {
      title: 'email',
      dataIndex: 'email',
      width: 200
    },
    {
      title: 'gender',
      dataIndex: 'gender',
      width: 100,
      render: (gender) => (!!gender ? t(`gender-${gender}`) : NO_DATA)
    },
    {
      title: 'old',
      dataIndex: 'year',
      width: 100,
      render: (value) => {
        return getOldPatient(value) || NO_DATA
      }
    },
    {
      title: 'address',
      dataIndex: 'address',
      width: 240,
      render: (value) => <Tooltip title={value}>{value}</Tooltip>
    },
    {
      title: 'created-at',
      dataIndex: 'created_at',
      width: 100,
      render: (value, row) =>
        !!row?.user_code ? formatDate(value) : DATE_EMPTY
    },
    {
      title: 'booking-status',
      dataIndex: 'have_upcoming_booking',
      width: 164,
      render: (haveUpcomingBooking) => (
        <Status
          statusColor={haveUpcomingBooking ? 'green' : 'red'}
          title={haveUpcomingBooking ? t('has-booking') : t('no-has-booking')}
        />
      )
    },
    {
      title: 'action',
      dataIndex: 'status',
      fixed: 'right',
      align: 'left',
      width: 212,
      render: (_, patient: IPatient) => (
        <ActionPatient patient={patient} onAction={onAction} />
      )
    }
  ]

  return {
    columnsBooking: createColumns(getBookingColumns()),
    columnsPatient: createColumns(getPatientColumns())
  }
}

export default useColumnBooking
